import { memo, useCallback, useMemo } from "react";

import { ColorSwatch } from "@react/components/ColorSwatch";
import { Icon, type IconName } from "@react/components/Icon";
import { VariantSelector } from "@react/components/VariantSelector/VariantSelector.data";
import { handleize, findSelectedOption } from "@react/utilities";
import {
  type CartProductVariant,
  type CartRenderVariantOptions,
} from "@tablescape/state";
import { tw } from "classnames";

type Props = {
  productName?: string;
  selected: CartProductVariant | undefined;
  setSelected: (variant: CartProductVariant["id"]) => void;
  showProductName?: boolean;
  size?: "tiny" | "mini" | "regular";
  variants: CartProductVariant[];
};

const ArtworkVariantSelectorBase = ({
  productName,
  selected,
  setSelected,
  showProductName,
  size = "regular",
  variants,
}: Props) => {
  const renderOption = useCallback(
    ({
      active,
      checked,
      currentOption,
      currentVariant,
    }: CartRenderVariantOptions) => (
      <ColorSwatch
        active={active}
        alt={currentVariant.title}
        available={currentVariant.availableForSale}
        backordered={
          !currentVariant.availableForSale && !currentVariant.disableWaitlist
        }
        checked={checked}
        color={tw("bg-bone-300")}
        optionLabel={currentVariant.optionLabel}
        size={size}
      >
        {currentOption && (
          <Icon
            className={tw("w-4", "h-4")}
            name={`swatch-${handleize(currentOption.value)}` as IconName}
          />
        )}
      </ColorSwatch>
    ),
    [size]
  );

  const artworkSelectedOption = useMemo(
    () =>
      selected
        ? findSelectedOption({ type: "artwork", variant: selected })
        : false,
    [selected]
  );

  return artworkSelectedOption ? (
    <VariantSelector
      productName={productName}
      renderOption={renderOption}
      selected={selected}
      selectedOption={artworkSelectedOption}
      setSelected={setSelected}
      showProductName={showProductName}
      type="artwork"
      variants={variants}
    />
  ) : null;
};

export const ArtworkVariantSelector = memo(ArtworkVariantSelectorBase);
